import http from '@/services/http';

/**
 * API client for the delivery-routes module (`rutas-reparto`, Trello card #404
 * "Rutas del día"). Contract given by the parallel backend agent — same base
 * `rutas-reparto`, same central `http` instance used by the rest of the app
 * (never raw axios). `obtenerRepartidores` reuses the existing source
 * (`GET usuarios`, filtered client-side by `id_rol === 7`) already used by
 * `Deposito.vue#cargarRepartidores` (same pattern also duplicated in
 * `AdminVentas.vue#cargarRepartidores`) — no `GET /filtros/repartidores`
 * endpoint exists in the front today.
 */
export default {
  // `incluirAtrasados` (seguimiento #414 "Incluir atrasados"): solo se manda
  // `incluir_atrasados=true` cuando es truthy — omitido (no `=false`) en
  // cualquier otro caso, para no ensuciar la query del caso por defecto.
  obtenerPedidosDisponibles({ fecha, id_repartidor, incluirAtrasados } = {}) {
    const params = { fecha, id_repartidor };
    if (incluirAtrasados) {
      params.incluir_atrasados = true;
    }
    return http.get('rutas-reparto/pedidos-disponibles', { params });
  },

  obtenerRutas({ fecha, id_repartidor, estado } = {}) {
    return http.get('rutas-reparto', { params: { fecha, id_repartidor, estado } });
  },

  obtenerRuta(idRuta) {
    return http.get(`rutas-reparto/${idRuta}`);
  },

  crearRuta({ fecha, id_repartidor, pedidos, observaciones }) {
    return http.post('rutas-reparto', { fecha, id_repartidor, pedidos, observaciones });
  },

  actualizarPedidos(idRuta, { agregar = [], quitar = [] } = {}) {
    return http.put(`rutas-reparto/${idRuta}/pedidos`, { agregar, quitar });
  },

  actualizarOrden(idRuta, pedidos) {
    return http.put(`rutas-reparto/${idRuta}/orden`, { pedidos });
  },

  ordenarPorCercania(idRuta) {
    return http.post(`rutas-reparto/${idRuta}/ordenar-por-cercania`);
  },

  actualizarEstado(idRuta, estado) {
    return http.put(`rutas-reparto/${idRuta}/estado`, { estado });
  },

  obtenerPdf(idRuta) {
    return http.get(`rutas-reparto/${idRuta}/pdf`, { responseType: 'blob' });
  },

  obtenerRepartidores() {
    return http.get('usuarios');
  },

  // Card #414 "Rutas del día": optimización de recorrido (Google Routes,
  // resuelto por el backend) y lectura del recorrido ya calculado.
  optimizarRuta(idRuta, { volver_al_centro, id_centro, aplicar = true } = {}) {
    return http.post(`rutas-reparto/${idRuta}/optimizar`, { volver_al_centro, id_centro, aplicar });
  },

  obtenerRecorrido(idRuta, { volver_al_centro } = {}) {
    return http.get(`rutas-reparto/${idRuta}/recorrido`, { params: { volver_al_centro } });
  },

  // Card #416 "Rutas despachables": despachar (enviar al repartidor), iniciar
  // el reparto (mueve los pedidos Preparado -> En reparto, no atómico) y
  // marcar cada parada como entregada/no entregada. Mismo `http` central.
  enviarRuta(idRuta) {
    return http.post(`rutas-reparto/${idRuta}/enviar`);
  },

  iniciarRuta(idRuta) {
    return http.post(`rutas-reparto/${idRuta}/iniciar`);
  },

  marcarParada(idRuta, idPedido, { estado, motivo_no_entrega } = {}) {
    return http.put(`rutas-reparto/${idRuta}/paradas/${idPedido}`, { estado, motivo_no_entrega });
  },

  // Seguimiento #416: reasigna el repartidor de la ruta sin cancelarla.
  // Sólo permitido en armada/enviada — el backend responde 409
  // RUTA_YA_INICIADA una vez en_reparto (ver store rutasReparto.js).
  reasignarRepartidor(idRuta, idRepartidor) {
    return http.put(`rutas-reparto/${idRuta}/repartidor`, { id_repartidor: idRepartidor });
  },

  // Rutas del repartidor logueado. No hay un endpoint dedicado confirmado por
  // el backend todavía (fuera del contrato de #416) — reutiliza la misma
  // base `rutas-reparto/mis-rutas` por analogía con el resto del módulo;
  // ajustar acá si el backend expone otra ruta.
  obtenerMisRutas({ fecha, estado } = {}) {
    return http.get('rutas-reparto/mis-rutas', { params: { fecha, estado } });
  },
};
